/**
 * Auto Save - automatic game saving at intervals
 */

import type { SaveData } from './SaveData';
import { storageManager } from './StorageManager';

const AUTO_SAVE_SLOT = 99; // Special slot for auto-saves
const AUTO_SAVE_INTERVAL = 5 * 60 * 1000; // 5 minutes in ms

export class AutoSave {
  private timer: number | null = null;
  private serializeCallback: (() => SaveData) | null = null;
  private enabled: boolean = true;
  private lastSaveTime: number = 0;

  /**
   * Set the serialization callback
   */
  setSerializeCallback(callback: () => SaveData): void {
    this.serializeCallback = callback;
  }

  /**
   * Start auto-save timer
   */
  start(): void {
    if (this.timer) {
      this.stop();
    }

    this.timer = window.setInterval(() => {
      this.performAutoSave();
    }, AUTO_SAVE_INTERVAL);
  }

  /**
   * Stop auto-save timer
   */
  stop(): void {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Enable or disable auto-save
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled && !this.timer) {
      this.start();
    } else if (!enabled && this.timer) {
      this.stop();
    }
  }

  /**
   * Check if auto-save is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Perform an auto-save
   */
  async performAutoSave(): Promise<boolean> {
    if (!this.enabled || !this.serializeCallback) {
      return false;
    }

    try {
      const saveData = this.serializeCallback();
      saveData.name = `自动存档 - ${new Date().toLocaleTimeString()}`;

      await storageManager.init();
      const success = await storageManager.save(AUTO_SAVE_SLOT, saveData);

      if (success) {
        this.lastSaveTime = Date.now();
        console.log('Auto-save complete');
      }

      return success;
    } catch (error) {
      console.error('Auto-save failed:', error);
      return false;
    }
  }

  /**
   * Load the auto-save
   */
  async loadAutoSave(): Promise<SaveData | null> {
    await storageManager.init();
    return storageManager.load(AUTO_SAVE_SLOT);
  }

  /**
   * Check if an auto-save exists
   */
  async hasAutoSave(): Promise<boolean> {
    const save = await this.loadAutoSave();
    return save !== null;
  }

  /**
   * Get time since last auto-save
   */
  getTimeSinceLastSave(): number {
    if (this.lastSaveTime === 0) return Infinity;
    return Date.now() - this.lastSaveTime;
  }

  /**
   * Get next auto-save time
   */
  getNextAutoSaveTime(): number {
    if (!this.timer || !this.enabled) return Infinity;
    return AUTO_SAVE_INTERVAL - (Date.now() - this.lastSaveTime);
  }

  dispose(): void {
    this.stop();
    this.serializeCallback = null;
  }
}

// Singleton instance
export const autoSave = new AutoSave();
